import React, {useState, useCallback} from 'react';
import {View, StyleSheet, FlatList, RefreshControl, ActivityIndicator, Image} from 'react-native';
import {Text, Snackbar} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import GradientHeader from '../../components/common/GradientHeader';
import {useFocusEffect} from '@react-navigation/native';
import {workerApi} from '../../services/api/workerApi';
import {colors, spacing} from '../../theme';

const TaskAssignmentDetailScreen = ({navigation, route}) => {
  const {workerId, workerName, taskTypeId, taskTypeName} = route.params;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const params = {workerId};
      if (taskTypeId) {
        params.taskTypeId = taskTypeId;
      }
      const response = await workerApi.getTaskAssignments(params);
      setData(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load task details');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [workerId, taskTypeId]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const formatDate = dateStr => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'});
  };

  const title = taskTypeName ? `${workerName} (${taskTypeName})` : workerName;

  const renderItem = ({item}) => (
    <View style={styles.card}>
      <View style={styles.imageContainer}>
        {item.clothImageUrl ? (
          <Image
            source={{uri: item.clothImageUrl}}
            style={styles.clothImage}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.placeholderImage}>
            <MaterialCommunityIcons name="tshirt-crew-outline" size={36} color={colors.textLight} />
          </View>
        )}
        <View style={styles.instanceCodeOverlay}>
          <Text style={styles.instanceCodeText}>{item.instanceCode}</Text>
        </View>
      </View>
      <View style={styles.detailsContainer}>
        <Text style={styles.catalogItemName}>{item.catalogItemName}</Text>
        <View style={styles.metaRow}>
          <MaterialCommunityIcons name="receipt" size={13} color={colors.textSecondary} />
          <Text style={styles.metaText}>#{item.orderNumber}</Text>
        </View>
        {item.customerCity ? (
          <View style={styles.metaRow}>
            <MaterialCommunityIcons name="map-marker-outline" size={13} color={colors.textSecondary} />
            <Text style={styles.metaText}>{item.customerCity}</Text>
          </View>
        ) : null}
        <View style={styles.metaRow}>
          <MaterialCommunityIcons name="calendar-clock-outline" size={13} color={colors.textSecondary} />
          <Text style={styles.metaText}>Due {formatDate(item.deliveryDate)}</Text>
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <GradientHeader
        title={title}
        onBack={() => navigation.goBack()}
      />

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={data}
          keyExtractor={item => String(item.instanceTaskId)}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={() => fetchData(true)} colors={[colors.primary]} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name="clipboard-check-outline" size={48} color={colors.textLight} />
              <Text style={styles.emptyText}>No tasks found</Text>
            </View>
          }
        />
      )}

      <Snackbar
        visible={!!error}
        onDismiss={() => setError('')}
        duration={3000}>
        {error}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.sm, paddingBottom: spacing.xl, gap: spacing.sm},

  // Card
  card: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  imageContainer: {
    width: 120,
    height: 110,
    backgroundColor: colors.background,
  },
  clothImage: {
    width: '100%',
    height: '100%',
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  instanceCodeOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingVertical: 3,
    paddingHorizontal: 8,
  },
  instanceCodeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  detailsContainer: {
    flex: 1,
    padding: spacing.md,
    justifyContent: 'center',
    gap: 4,
  },
  catalogItemName: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontSize: 13,
    color: colors.textSecondary,
  },

  // Empty
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },
});

export default TaskAssignmentDetailScreen;
